import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  ShieldCheck,
  WalletCards,
  X
} from 'lucide-react';

const actions = [
  { label: 'Add expense', detail: 'Record money spent', icon: ArrowUpRight, to: '/form?type=expense', tone: 'bg-rose-50 text-rose-600' },
  { label: 'Add income', detail: 'Salary or other income', icon: ArrowDownLeft, to: '/form?type=income', tone: 'bg-emerald-50 text-emerald-600' },
  { label: 'Add savings', detail: 'Grow a savings balance', icon: Landmark, to: '/money-tracker?tab=savings&add=1', tone: 'bg-violet-50 text-violet-600' },
  { label: 'Lend money', detail: 'Track money someone owes you', icon: WalletCards, to: '/money-tracker?tab=lent&add=1', tone: 'bg-amber-50 text-amber-700' },
  { label: 'Add safekeeping', detail: 'Track money held for someone', icon: ShieldCheck, to: '/money-tracker?tab=safekeeping&add=1', tone: 'bg-sky-50 text-sky-600' }
];

export function QuickAddSheet({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/35 px-3 backdrop-blur-[2px] md:items-center"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      role="presentation"
    >
      <section
        className="m3-sheet w-full max-w-lg rounded-t-[32px] bg-white px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 md:rounded-[32px] md:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-add-title"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-300 md:hidden" />
        <div className="mb-5 flex items-start justify-between">
          <div>
            <p className="m3-label">Quick add</p>
            <h2 id="quick-add-title" className="mt-1 text-2xl font-extrabold tracking-tight text-slate-950">What would you like to add?</h2>
          </div>
          <button type="button" onClick={onClose} className="m3-icon-button" aria-label="Close quick add">
            <X size={20} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {actions.map(({ label, detail, icon: Icon, to, tone }) => (
            <Link
              key={label}
              to={to}
              onClick={onClose}
              className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-4 transition active:scale-[0.98] hover:bg-slate-50"
            >
              <span className={`mb-4 grid h-11 w-11 place-items-center rounded-2xl ${tone}`}>
                <Icon size={21} strokeWidth={2} />
              </span>
              <span className="block text-sm font-extrabold text-slate-950">{label}</span>
              <span className="mt-1 block text-xs font-medium leading-4 text-slate-500">{detail}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
